import { createFileRoute } from "@tanstack/react-router";
import { generateBuilding } from "building-generation";
import { groundLevelId } from "building-generation/elements/level/levelTools";
import type { Building } from "building-types/building/buildingTypes";
import type { Doc } from "building-types/doc";
import { FaceCategory, allFaceCategories, defaultIterateFaceCategories } from "building-types/materials/faceCategories";
import type { ElevationReferenceFrame } from "building-types/views/elevationReferenceFrame";
import { ViewEntityTable } from "building-types/views/viewEntityTable";
import { DEFAULT_EYE_LEVEL } from "common/constants/camera";
import hashString from "common/utils/hashString";
import { RandomSourceWithSeed } from "common/utils/random";
import { uploadFile } from "common/utils/uploadFile";
import { FILE_EXT, docParse } from "doc/docMarshal";
import { DrawingCameraType, type DrawingCamera } from "doc/plans/drawingCameraTypes";
import { arc2ByCenterStartAngle } from "geom/arc";
import { faceNew } from "geom/brep";
import { buildBSPNode, removeCoplanarLinesBSP } from "geom/bsp";
import type { PlaneBSP } from "geom/bspTypes";
import { extrudeFaceAlongNormal, extrusionToBRep } from "geom/extrude";
import type { Mat4 } from "geom/mat4";
import { mat4Mult, mat4Scale, mat4Translate } from "geom/mat4";
import { planeNew } from "geom/plane";
import { polyLine2Centroid, polyLine2SetCCW, polyLine2SetCW } from "geom/polyLine2";
import { polyLine2New } from "geom/polyLine2Types";
import { UVGeneration } from "geom/uv";
import { vec3Add, vec3Scale, type Vec3 } from "geom/vec3";
import { defaultCameraSettings, defaultExteriorCameraPosition } from "gfx/CameraSettings";
import { buildPolygonsFromFaceGroups, type FaceGroupBSP } from "lib/house/faceGroupBSP";
import type { WASMState } from "lib/house/plans/assets/loadWasm";
import { loadWasm } from "lib/house/plans/assets/loadWasm";
import { collectBuildingFacesByEntityId } from "lib/house/plans/collectBuildingFaces";
import { drawingCameraToMatrices } from "lib/house/plans/drawingCameraToMatrices";
import { getPolygonsAabb } from "lib/house/plans/perspective";
import { GROUND_FACES_PRIORITY } from "lib/house/plans/perspectiveConstants";
import { renderPerspectiveEntityPolygons } from "lib/house/plans/perspectiveEntities";
import type { SceneBSP } from "lib/house/plans/SceneBSP";
import {
  elevationReferenceFrameToEyePosition,
  elevationReferenceFrameToViewDirection,
} from "lib/studio/viewPlaneTools";
import type { ReactNode } from "react";
import { createContext, useContext, useEffect, useState } from "react";

const initial: WASMState = {};

const WASMContext = createContext(initial);

const WASMContextProvider: React.FC<WASMContextProviderProps> = ({ children }) => {
  const [state, setState] = useState<WASMState>(initial);

  // This has to run only once: https://github.com/rustwasm/wasm-bindgen/issues/3153
  // Though, in development React renders twice when Strict Mode is enabled: https://reactjs.org/docs/strict-mode.html
  // That's why it must be limited to a single mount run
  useEffect(() => {
    void (async () =>
      loadWasm().then((wasm) => {
        setState(wasm);
      }))();
  }, [state]);

  return <WASMContext.Provider value={state}>{children}</WASMContext.Provider>;
};

interface WASMContextProviderProps {
  children: ReactNode;
}

type ExampleType =
  | "coplanar-faces"
  | "building"
  | "ground-plane"
  | "cubes"
  | "face-with-hole"
  | "face-with-holes"
  | "simple-face";

interface ExampleOption {
  value: ExampleType;
  label: string;
}

const EXAMPLE_OPTIONS: ExampleOption[] = [
  {
    value: "cubes",
    label: "Cubes",
  },
  {
    value: "simple-face",
    label: "Simple Face",
  },
  {
    value: "coplanar-faces",
    label: "Coplanar Faces",
  },
  {
    value: "ground-plane",
    label: "Ground Plane",
  },
  {
    value: "face-with-hole",
    label: "Face with Hole",
  },
  {
    value: "face-with-holes",
    label: "Face with Holes",
  },
  {
    value: "building",
    label: "Building",
  },
];

interface ExampleCamera {
  isOrtho: boolean;
  eyeX: number;
  eyeY: number;
  eyeZ: number;
  targetX: number;
  targetY: number;
  targetZ: number;
  fovDegrees?: number;
}

interface Example {
  faces: FaceGroupBSP[];
  camera: ExampleCamera;
}

function makeDrawingCamera(camera: ExampleCamera, width: number, height: number, rotation: number): DrawingCamera {
  const dx = camera.eyeX - camera.targetX;
  const dy = camera.eyeY - camera.targetY;
  const cosR = Math.cos(rotation);
  const sinR = Math.sin(rotation);
  const newEyeX = camera.targetX + dx * cosR - dy * sinR;
  const newEyeY = camera.targetY + dx * sinR + dy * cosR;

  const eye: [number, number, number] = [newEyeX, newEyeY, camera.eyeZ];
  const target: [number, number, number] = [camera.targetX, camera.targetY, camera.targetZ];

  if (!camera.isOrtho) {
    return {
      type: DrawingCameraType.PERSPECTIVE,
      eye,
      target,
      up: [0, 0, 1],
      fovDegrees: camera.fovDegrees ?? defaultCameraSettings.fov,
      far: 10000,
      near: 28,
      aspect: width / height,
      viewCropAABB: {
        minX: 0,
        minY: 0,
        maxX: 1,
        maxY: 1,
      },
    };
  } else {
    return {
      type: DrawingCameraType.ORTHOGRAPHIC,
      eye,
      target,
      up: [0, 0, 1],
      left: -width / 2,
      right: width / 2,
      bottom: -height / 2,
      top: height / 2,
      near: 0.1,
      far: 100,
    };
  }
}

function generateExampleFaces(exampleType: ExampleType): Example {
  switch (exampleType) {
    case "cubes": {
      const count = 4;
      const scale = 100;
      const hscale = scale / 2;

      // Create a square face and extrude it to make a cube
      const squareLoop = polyLine2SetCW(
        polyLine2New([
          -hscale,
          -hscale, // bottom-left
          hscale,
          -hscale, // bottom-right
          hscale,
          hscale, // top-right
          -hscale,
          hscale, // top-left
          -hscale,
          -hscale, // close the loop
        ]),
      );

      const faces: FaceGroupBSP[] = [];

      const r = new RandomSourceWithSeed(0);

      const min = -100;
      const max = 100;

      for (let i = 0; i < count; i++) {
        const plane = planeNew(
          r.nextIntInRange(min, max),
          r.nextIntInRange(min, max),
          r.nextIntInRange(min, max),
          1,
          0,
          0,
          0,
          1,
          0,
        ); // XY plane at origin
        const baseFace = faceNew([squareLoop], plane);

        const extrusion = extrudeFaceAlongNormal(baseFace, scale); // extrude 2 units along normal
        const brep = extrusionToBRep(extrusion);

        faces.push({ faces: brep.faces, uvGen: UVGeneration.GLOBAL_RECT_UV, entityId: `face-${i}` });
      }

      return {
        faces,
        camera: {
          isOrtho: true,
          eyeX: 400,
          eyeY: -400,
          eyeZ: 100,
          targetX: 0,
          targetY: 0,
          targetZ: 0,
        },
      };
    }

    case "ground-plane": {
      const groundPlaneRadius = 5000.0;
      const groundLoop = arc2ByCenterStartAngle(0, 0, groundPlaneRadius, 0, -2 * Math.PI, undefined, 5, 5);
      const groundFace = faceNew([groundLoop], planeNew(0, 0, 0, 1, 0, 0, 0, 1, 0));

      const face = faceNew([polyLine2New([0, 0, 0, 6, 240, 6, 240, 0, 0, 0])], {
        originX: 238.25,
        originY: -2.25,
        originZ: -6,
        uX: -1,
        uY: 0,
        uZ: 0,
        vX: 0,
        vY: 0,
        vZ: -1,
      });

      return {
        faces: [
          { faces: [face], uvGen: UVGeneration.GLOBAL_RECT_UV, entityId: "face" },
          { faces: [groundFace], uvGen: UVGeneration.GLOBAL_RECT_UV, entityId: "ground-face" },
        ],
        camera: {
          isOrtho: false,
          fovDegrees: 50,
          eyeX: 185,
          eyeY: -940,
          eyeZ: 65,
          targetX: 45,
          targetY: -57,
          targetZ: 68,
        },
      };
    }

    case "simple-face": {
      const loop = polyLine2SetCW(polyLine2New([0, 0, 100, 0, 100, 100, 0, 100, 0, 0]));
      const plane = planeNew(-50, 0, 0, 1, 0, 0, 0, 0, 1); // XZ plane at origin

      return {
        faces: [{ faces: [faceNew([loop], plane)], uvGen: UVGeneration.GLOBAL_RECT_UV, entityId: "face" }],
        camera: {
          isOrtho: false,
          eyeX: 250,
          eyeY: -250,
          eyeZ: 150,
          targetX: 0,
          targetY: 0,
          targetZ: 0,
        },
      };
    }

    case "coplanar-faces": {
      const loop = polyLine2SetCW(polyLine2New([0, 0, 100, 0, 100, 100, 0, 100, 0, 0]));
      const plane = planeNew(0, 0, 0, 1, 0, 0, 0, 0, 1); // XZ plane at origin
      const plane2 = planeNew(-100, 0, 0, 1, 0, 0, 0, 0, 1); // XZ plane at origin

      return {
        faces: [
          { faces: [faceNew([loop], plane)], uvGen: UVGeneration.GLOBAL_RECT_UV, entityId: "face" },
          { faces: [faceNew([loop], plane2)], uvGen: UVGeneration.GLOBAL_RECT_UV, entityId: "face2" },
        ],
        camera: {
          isOrtho: false,
          eyeX: 250,
          eyeY: -250,
          eyeZ: 150,
          targetX: 0,
          targetY: 0,
          targetZ: 0,
        },
      };
    }

    case "face-with-hole": {
      const outerLoop = polyLine2SetCW(polyLine2New([0, 0, 100, 0, 100, 100, 0, 100, 0, 0]));
      const innerLoop1 = polyLine2SetCCW(polyLine2New([25, 25, 75, 25, 75, 75, 25, 75, 25, 25]));

      const plane = planeNew(0, 0, 0, 1, 0, 0, 0, 0, 1); // XZ plane at origin

      return {
        faces: [
          {
            faces: [faceNew([outerLoop, innerLoop1], plane)],
            uvGen: UVGeneration.GLOBAL_RECT_UV,
            entityId: "face",
          },
        ],
        camera: {
          isOrtho: false,
          eyeX: 250,
          eyeY: -250,
          eyeZ: 150,
          targetX: 0,
          targetY: 0,
          targetZ: 0,
        },
      };
    }

    case "face-with-holes": {
      const outerLoop = polyLine2SetCW(polyLine2New([0, 0, 300, 0, 300, 200, 0, 200, 0, 0]));
      const innerLoop1 = polyLine2SetCCW(polyLine2New([50, 50, 120, 50, 120, 120, 50, 120, 50, 50]));
      const innerLoop2 = polyLine2SetCCW(polyLine2New([180, 50, 250, 50, 250, 120, 180, 120, 180, 50]));

      const plane = planeNew(0, 0, 0, 1, 0, 0, 0, 0, 1); // XZ plane at origin

      return {
        faces: [
          {
            faces: [faceNew([outerLoop, innerLoop1, innerLoop2], plane)],
            uvGen: UVGeneration.GLOBAL_RECT_UV,
            entityId: "face",
          },
        ],
        camera: {
          isOrtho: false,
          eyeX: 350,
          eyeY: -350,
          eyeZ: 200,
          targetX: 150,
          targetY: 0,
          targetZ: 100,
        },
      };
    }

    default:
      return generateExampleFaces("cubes");
  }
}

function WASMCanvas() {
  const [selectedExample, setSelectedExample] = useState<ExampleType>("cubes");
  const [selectedFaceCategories, setSelectedFaceCategories] = useState<Set<FaceCategory>>(
    new Set(defaultIterateFaceCategories),
  );

  const ctx = useContext(WASMContext);

  const [scale, setScale] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [doc, setDoc] = useState<Doc | null>(null);
  const [building, setBuilding] = useState<Building | null>(null);
  const [showLines, setShowLines] = useState(true);
  const [showOcclusionPolygons, setShowOcclusionPolygons] = useState(false);
  const [lines, setLines] = useState<any[]>([]);
  const [polygons, setPolygons] = useState<any[]>([]);
  const [bsp, setBsp] = useState<[typeof ctx.wasm, number] | null>(null);
  const [renderData, setRenderData] = useState<{
    finalProjectionMatrix: Mat4;
    viewMatrix: Mat4;
    projectionMatrix: Mat4;
    eye: Vec3;
    target: Vec3;
    isPerspective: boolean;
    clippingPlanes: PlaneBSP[];
  } | null>(null);
  const [bspFromFacesTime, setBspFromFacesTime] = useState(0);
  const [renderBSPToLinesTime, setRenderBSPToLinesTime] = useState(0);
  const [renderBSPToPolygonsTime, setRenderBSPToPolygonsTime] = useState(0);
  const [orthographic, setOrthographic] = useState<boolean>(false);
  const [elevationView, setElevationView] = useState<ElevationReferenceFrame>("FRONT");
  const [enableBuildBSPNodeTimer, setEnableBuildBSPNode] = useState<boolean>(false);
  const [enableRenderPerspectiveEntityPolygons, setEnableRenderPerspectiveEntityPolygons] = useState<boolean>(false);
  const [buildBSPNodeTime, setBuildBSPNodeTime] = useState<number>(0);
  const [renderPerspectiveEntityPolygonsTime, setRenderPerspectiveEntityPolygonsTime] = useState<number>(0);
  const [facesToProcess, setFacesToProcess] = useState<FaceGroupBSP[]>([]);

  useEffect(() => {
    void (async () => {
      if (!doc) {
        return;
      }

      const building = await generateBuilding({ name: "Foo", doc });

      setBuilding(building);
    })();
  }, [doc]);

  useEffect(() => {
    if (!ctx.wasm) {
      return;
    }

    let facesToProcess: FaceGroupBSP[] = [];

    if (selectedExample === "building") {
      if (building) {
        const entityTable: ViewEntityTable = new ViewEntityTable();
        const groups = collectBuildingFacesByEntityId({
          building,
          enableHatchMaterials: false,
          buildMask: selectedFaceCategories,
          entityTable,
          addEntityIds: true,
        });
        for (const groupId in groups) {
          const group = groups[groupId];
          facesToProcess.push(...group);
        }
      }
    } else {
      facesToProcess = generateExampleFaces(selectedExample).faces;
    }

    setFacesToProcess(facesToProcess);

    const t0 = performance.now();

    const newBsp = ctx.wasm.bspFromFaceGroups(facesToProcess, true);
    const t1 = performance.now();
    setBspFromFacesTime(t1 - t0);
    setBsp([ctx.wasm, newBsp]);

    return () => {
      if (newBsp) {
        ctx.wasm?.disposeBSP(newBsp);
      }
      setBsp(null);
    };
  }, [ctx.wasm, selectedExample, building, enableBuildBSPNodeTimer, selectedFaceCategories]);

  useEffect(() => {
    if (enableBuildBSPNodeTimer) {
      void (async () => {
        // wait one second to make sure no other work is being handled
        await new Promise((resolve) => setTimeout(resolve, 1000));

        const t0 = performance.now();

        const polys = await buildPolygonsFromFaceGroups(facesToProcess, GROUND_FACES_PRIORITY, false);

        const root = buildBSPNode({ polygons: polys });
        const aabb = getPolygonsAabb(polygons);
        removeCoplanarLinesBSP(root, polys);

        const t1 = performance.now();
        setBuildBSPNodeTime(t1 - t0);

        setJSBSP({ root, aabb });
      })();
    } else {
      setBuildBSPNodeTime(0);
    }
  }, [facesToProcess]);

  const [jsBSP, setJSBSP] = useState<SceneBSP | null>(null);

  useEffect(() => {
    if (enableRenderPerspectiveEntityPolygons && jsBSP && renderData) {
      const t0 = performance.now();

      renderPerspectiveEntityPolygons({
        sceneBSP: jsBSP,
        viewMatrix: renderData.viewMatrix,
        isPerspectiveMatrix: false,
        prjMatrix: renderData.projectionMatrix,
        clippingPlanes: renderData.clippingPlanes,
        unionEntityGroups: false,
      });

      const t1 = performance.now();

      setRenderPerspectiveEntityPolygonsTime(t1 - t0);
    } else {
      setRenderPerspectiveEntityPolygonsTime(0);
    }
  }, [jsBSP, enableRenderPerspectiveEntityPolygons, enableBuildBSPNodeTimer, renderData]);

  useEffect(() => {
    const width = parseFloat(document.getElementById("canvas")?.getAttribute("width") ?? "1000");
    const height = parseFloat(document.getElementById("canvas")?.getAttribute("height") ?? "1000");

    let cameraToUse: DrawingCamera;

    if (selectedExample === "building") {
      if (!building) {
        return;
      }

      // This is the logic from pdf cover pages
      const baseLevelId = groundLevelId(building.levels);
      if (!baseLevelId) {
        return;
      }

      const floorPlate =
        Object.values(building.floorPlates).length > 0
          ? building.floorPlates[baseLevelId].perimeterPolyLine2s[0]
          : null;

      let lookAt: Vec3;

      const defaultExteriorCamera = building.defaultExteriorCamera ?? defaultExteriorCameraPosition;
      let eye = [defaultExteriorCamera.x, defaultExteriorCamera.y, defaultExteriorCamera.z];

      if (orthographic) {
        eye = vec3Add(
          elevationReferenceFrameToEyePosition[elevationView][0],
          elevationReferenceFrameToEyePosition[elevationView][1],
          elevationReferenceFrameToEyePosition[elevationView][2],
          building.buildingBounds.centroidX,
          building.buildingBounds.centroidY,
          building.buildingBounds.centroidZ,
        ).slice();

        const [viewDirX, viewDirY, viewDirZ] = vec3Scale(
          elevationReferenceFrameToViewDirection[elevationView][0],
          elevationReferenceFrameToViewDirection[elevationView][1],
          elevationReferenceFrameToViewDirection[elevationView][2],
          1000,
        );
        lookAt = vec3Add(viewDirX, viewDirY, viewDirZ, eye[0], eye[1], eye[2]);
      } else {
        const lookAt2 = floorPlate ? polyLine2Centroid(floorPlate) ?? [0, 0] : [0, 0];

        lookAt = [lookAt2[0], lookAt2[1], DEFAULT_EYE_LEVEL];
      }

      cameraToUse = makeDrawingCamera(
        {
          isOrtho: orthographic,
          eyeX: eye[0],
          eyeY: eye[1],
          eyeZ: eye[2],
          targetX: lookAt[0],
          targetY: lookAt[1],
          targetZ: lookAt[2],
          fovDegrees: 50,
        },
        width,
        height,
        orthographic ? 0 : rotation,
      );
    } else {
      const example = generateExampleFaces(selectedExample);
      cameraToUse = makeDrawingCamera(
        {
          ...example.camera,
          isOrtho: orthographic,
        },
        width,
        height,
        rotation,
      );
    }

    const eye = cameraToUse.eye;
    const target = cameraToUse.target;

    const { viewMatrix, projectionMatrix, clippingPlanes } = drawingCameraToMatrices(cameraToUse);

    const viewPortTranslateMatrix = mat4Translate(1 / scale, 1 / scale, 0.0);
    const viewPortScaleMatrix = mat4Scale(scale * width * 0.5, scale * height * 0.5, 1);
    const viewportMatrix = mat4Mult(viewPortScaleMatrix, viewPortTranslateMatrix);
    const viewportProjectionMatrix = mat4Mult(viewportMatrix, projectionMatrix);

    const finalProjectionMatrix = mat4Mult(viewportProjectionMatrix, viewMatrix);

    setRenderData({
      finalProjectionMatrix,
      eye,
      target,
      viewMatrix,
      projectionMatrix,
      isPerspective: cameraToUse.type === DrawingCameraType.PERSPECTIVE,
      clippingPlanes: clippingPlanes ?? [],
    });
  }, [selectedExample, building, scale, rotation, orthographic, elevationView]);

  useEffect(() => {
    if (!ctx.wasm || !bsp || !renderData || !showLines) {
      setLines([]);
      return;
    }

    // During hot reload, the wasm module gets swapped out and the bsp becomes invalid
    if (bsp[0] !== ctx.wasm) {
      setLines([]);
      return;
    }

    const { finalProjectionMatrix, eye, target, clippingPlanes, isPerspective } = renderData;

    const t0 = performance.now();
    const newLines = ctx.wasm.renderBSPToLines(
      bsp[1],
      finalProjectionMatrix,
      isPerspective,
      eye[0],
      eye[1],
      eye[2],
      target[0],
      target[1],
      target[2],
      clippingPlanes,
    );

    const t1 = performance.now();
    setRenderBSPToLinesTime(t1 - t0);
    setLines(newLines);
  }, [bsp, ctx.wasm, renderData, showLines]);

  useEffect(() => {
    if (!ctx.wasm || !bsp || !renderData || !showOcclusionPolygons) {
      setPolygons([]);
      return;
    }

    // During hot reload, the wasm module gets swapped out and the bsp becomes invalid
    if (bsp[0] !== ctx.wasm) {
      setLines([]);
      return;
    }

    const { finalProjectionMatrix, eye, target, clippingPlanes, isPerspective } = renderData;
    const t0 = performance.now();
    const newPolygons = ctx.wasm.renderBSPToPolygons(
      bsp[1],
      finalProjectionMatrix,
      isPerspective,
      eye[0],
      eye[1],
      eye[2],
      target[0],
      target[1],
      target[2],
      clippingPlanes,
    );
    const t1 = performance.now();
    setRenderBSPToPolygonsTime(t1 - t0);
    setPolygons(newPolygons);
  }, [bsp, ctx.wasm, renderData, showOcclusionPolygons]);

  useEffect(() => {
    const randomSource = new RandomSourceWithSeed(0);

    // render each polygon as a path by first getting the canvas element and then using the ctx to draw the path
    const canvas = document.getElementById("canvas") as HTMLCanvasElement;
    if (canvas) {
      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.save();
        ctx.fillStyle = "white";
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        ctx.transform(1, 0, 0, -1, 0, canvas.height);

        ctx.save();
        ctx.strokeStyle = "black";
        ctx.lineWidth = 2;

        const jitter = 0;

        lines.forEach((line) => {
          ctx.save();
          ctx.translate(randomSource.nextIntInRange(-jitter, jitter), randomSource.nextIntInRange(-jitter, jitter));

          ctx.beginPath();
          ctx.moveTo(line.startX, line.startY);
          ctx.lineTo(line.endX, line.endY);
          ctx.stroke();
          ctx.restore();
        });

        ctx.restore();

        if (showOcclusionPolygons) {
          polygons.forEach((polygon) => {
            if (polygon.vertices.length < 3) {
              return;
            }

            const entityTypeColorSource = new RandomSourceWithSeed(polygon.data ? hashString(polygon.data) : 0);

            const color = `rgba(${entityTypeColorSource.nextIntInRange(0, 255)}, ${entityTypeColorSource.nextIntInRange(
              0,
              255,
            )}, ${entityTypeColorSource.nextIntInRange(0, 255)}, 0.2)`;

            ctx.save();

            ctx.translate(randomSource.nextIntInRange(-jitter, jitter), randomSource.nextIntInRange(-jitter, jitter));

            ctx.fillStyle = color;

            ctx.beginPath();

            ctx.moveTo(polygon.vertices[0].x, polygon.vertices[0].y);

            polygon.vertices.slice(1).forEach((point) => {
              ctx.lineTo(point.x, point.y);
            });

            ctx.closePath();
            ctx.fill();

            ctx.restore();
          });
        }

        ctx.restore();
      }
    }
  }, [lines, polygons, showOcclusionPolygons]);

  const openBuilding = async () => {
    const file = await uploadFile("application/json," + FILE_EXT);

    setDoc(docParse(file.body));
  };

  return (
    <>
      <div style={{ position: "absolute", top: "10px", right: "10px" }}>
        <table style={{ borderCollapse: "separate" }}>
          <tbody>
            <tr>
              <td style={{ width: "80px", padding: "8px" }}>
                <label>Example</label>
              </td>
              <td style={{ padding: "8px" }}>
                <select
                  value={selectedExample}
                  onChange={(e) => setSelectedExample(e.target.value as ExampleType)}
                  className="w-full p-0 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {EXAMPLE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </td>
            </tr>
            {selectedExample === "building" && (
              <>
                <tr>
                  <td style={{ padding: "8px" }} />
                  <td style={{ padding: "8px" }}>
                    <button onClick={openBuilding}>Open .home</button>
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: "8px" }}>
                    <label>Face Categories</label>
                  </td>
                  <td style={{ padding: "8px" }}>
                    <div style={{ marginBottom: "8px", display: "flex", gap: "8px" }}>
                      <button
                        onClick={() => setSelectedFaceCategories(new Set(allFaceCategories))}
                        style={{
                          padding: "4px 8px",
                          backgroundColor: "#f0f0f0",
                          border: "1px solid #ccc",
                          borderRadius: "4px",
                          cursor: "pointer",
                        }}
                      >
                        Enable All Categories
                      </button>
                      <button
                        onClick={() => setSelectedFaceCategories(new Set(defaultIterateFaceCategories))}
                        style={{
                          padding: "4px 8px",
                          backgroundColor: "#f0f0f0",
                          border: "1px solid #ccc",
                          borderRadius: "4px",
                          cursor: "pointer",
                        }}
                      >
                        Default Categories
                      </button>
                      <button
                        onClick={() => setSelectedFaceCategories(new Set())}
                        style={{
                          padding: "4px 8px",
                          backgroundColor: "#f0f0f0",
                          border: "1px solid #ccc",
                          borderRadius: "4px",
                          cursor: "pointer",
                        }}
                      >
                        Disable All Categories
                      </button>
                    </div>
                    <div style={{ maxHeight: "200px", overflowY: "auto", border: "1px solid #ccc", padding: "8px" }}>
                      {allFaceCategories.map((category) => (
                        <div key={category} style={{ marginBottom: "4px" }}>
                          <label style={{ display: "flex", alignItems: "center" }}>
                            <input
                              type="checkbox"
                              checked={selectedFaceCategories.has(category)}
                              onChange={(e) => {
                                const newSet = new Set(selectedFaceCategories);
                                if (e.target.checked) {
                                  newSet.add(category);
                                } else {
                                  newSet.delete(category);
                                }
                                setSelectedFaceCategories(newSet);
                              }}
                              style={{ marginRight: "8px" }}
                            />
                            {FaceCategory[category]}
                          </label>
                        </div>
                      ))}
                    </div>
                  </td>
                </tr>
              </>
            )}
            <tr>
              <td style={{ width: "80px", padding: "8px" }}>
                <label>Zoom</label>
              </td>
              <td style={{ padding: "8px" }}>
                <input
                  type="range"
                  min="0.001"
                  max="5"
                  step="0.01"
                  value={scale}
                  onChange={(e) => setScale(parseFloat(e.target.value))}
                />
              </td>
            </tr>
            {(!orthographic || !building) && (
              <tr>
                <td style={{ width: "80px", padding: "8px" }}>
                  <label>Rotation</label>
                </td>
                <td style={{ padding: "8px" }}>
                  <input
                    type="range"
                    min="0"
                    max={2 * Math.PI}
                    step="0.01"
                    value={rotation}
                    onChange={(e) => setRotation(parseFloat(e.target.value))}
                  />
                </td>
              </tr>
            )}
            <tr>
              <td style={{ width: "80px", padding: "8px" }}>
                <label>Orthographic</label>
              </td>
              <td style={{ padding: "8px" }}>
                <input type="checkbox" checked={orthographic} onChange={(e) => setOrthographic(e.target.checked)} />
              </td>
            </tr>
            {orthographic && building && (
              <tr>
                <td style={{ width: "80px", padding: "8px" }}>
                  <label>Elevation</label>
                </td>
                <td style={{ padding: "8px" }}>
                  <select
                    defaultValue={elevationView}
                    onChange={(e) => setElevationView(e.target.value as ElevationReferenceFrame)}
                  >
                    <option value="FRONT">Front</option>
                    <option value="BACK">Back</option>
                    <option value="LEFT">Left</option>
                    <option value="RIGHT">Right</option>
                  </select>
                </td>
              </tr>
            )}
            <tr>
              <td style={{ width: "80px", padding: "8px" }}>
                <label>Render Polygons (Rust)</label>
              </td>
              <td style={{ padding: "8px" }}>
                <input
                  type="checkbox"
                  checked={showOcclusionPolygons}
                  onChange={(e) => setShowOcclusionPolygons(e.target.checked)}
                />
              </td>
            </tr>
            <tr>
              <td style={{ width: "80px", padding: "8px" }}>
                <label>Render Lines</label>
              </td>
              <td style={{ padding: "8px" }}>
                <input type="checkbox" checked={showLines} onChange={(e) => setShowLines(e.target.checked)} />
              </td>
            </tr>
            <tr>
              <td style={{ width: "80px", padding: "8px" }}>
                <label>Build BSP (JS)</label>
              </td>
              <td style={{ padding: "8px" }}>
                <input
                  type="checkbox"
                  checked={enableBuildBSPNodeTimer}
                  onChange={(e) => setEnableBuildBSPNode(e.target.checked)}
                />
              </td>
            </tr>

            <tr>
              <td style={{ width: "80px", padding: "8px" }}>
                <label>Render Polygons (JS)</label>
              </td>
              <td style={{ padding: "8px" }}>
                <input
                  type="checkbox"
                  checked={enableRenderPerspectiveEntityPolygons}
                  onChange={(e) => setEnableRenderPerspectiveEntityPolygons(e.target.checked)}
                />
              </td>
            </tr>

            <tr>
              <td style={{ padding: "8px" }}>
                <label>Build BSP (Rust)</label>
              </td>
              <td style={{ padding: "8px" }}>{bspFromFacesTime.toFixed(2)} ms</td>
            </tr>

            {enableBuildBSPNodeTimer && (
              <tr>
                <td style={{ padding: "8px" }}>
                  <label>Build BSP (JS)</label>
                </td>
                <td style={{ padding: "8px" }}>{buildBSPNodeTime.toFixed(2)} ms</td>
              </tr>
            )}
            {showOcclusionPolygons && (
              <tr>
                <td style={{ padding: "8px" }}>
                  <label>Render Polygons (Rust)</label>
                </td>
                <td style={{ padding: "8px" }}>{renderBSPToPolygonsTime.toFixed(2)} ms</td>
              </tr>
            )}
            {enableRenderPerspectiveEntityPolygons && (
              <tr>
                <td style={{ padding: "8px" }}>
                  <label>Render Polygons (JS)</label>
                </td>
                <td style={{ padding: "8px" }}>{renderPerspectiveEntityPolygonsTime.toFixed(2)} ms</td>
              </tr>
            )}
            {showLines && (
              <tr>
                <td style={{ padding: "8px" }}>
                  <label>Render Lines (Rust)</label>
                </td>
                <td style={{ padding: "8px" }}>{renderBSPToLinesTime.toFixed(2)} ms</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div>
        <canvas id="canvas" width="2400" height="1600" style={{ width: 1200, height: 800 }} />
      </div>
    </>
  );
}

function WasmDebugPage() {
  return (
    <WASMContextProvider>
      <style>{`
        * {
          font-family: Arial, sans-serif;
          font-size: 12px;
        }

        canvas {
          border: 2px solid #333;
          outline: 1px solid #666;
        }

        h1 {
          font-size: 18px;
          font-weight: bold;
        }

        h2 {
          font-size: 14px;
          font-weight: bold;
        }

        button {
          font-size: 12px;
        }

        select {
          font-size: 12px;
        }
      `}</style>
      <WASMCanvas />
    </WASMContextProvider>
  );
}

export const Route = createFileRoute("/debug/wasm")({
  component: WasmDebugPage,
});
